import type { ApiRequestConfig } from './api';
import { apiClient } from './api';
import type {
  ScheduleDashboardOccurrence,
  ScheduleDashboardResponse,
  TenantAthleteAvailability,
  TenantAthleteAvailabilityListResponse,
  TenantAthleteAvailabilityPayload,
  TenantCoachAvailability,
  TenantCoachAvailabilityPayload,
  TenantMakeupCreditUpdatePayload,
  TenantRosterCancellationPayload,
  TenantSessionFollowUpResponse,
  TenantSessionDetailResponse,
  TenantSessionAttendanceUpdatePayload,
  TenantSessionProgressNoteUpdatePayload,
  TenantSessionRecapMutationResponse,
  TenantSessionRecapPayload,
  TenantSessionRecapResponse,
  TenantSessionRosterAddPayload,
  TenantSessionRosterUpdatePayload,
  TenantProgram,
  TenantProgramRegistration,
  TenantProgramRegistrationUpdatePayload,
  TenantProgramUpsertPayload,
  TenantScheduleCancelPayload,
  TenantScheduleCreatePayload,
  TenantScheduleUpdatePayload,
  TenantSlotFillerApplyPayload,
  TenantSlotFillerSuggestionResponse,
  TenantSmartSchedulePayload,
  TenantSmartScheduleSuggestionResponse,
  TenantTimePoll,
  TenantTimePollBuilderOptions,
  TenantTimePollSavePayload,
  TenantBookableOffering,
  TenantBookableOfferingPayload,
  TenantScheduleDraft,
  TenantScheduleDraftUpdatePayload,
} from '../types';

export async function getScheduleBookableOfferings(): Promise<TenantBookableOffering[]> {
  const response = await apiClient.get<TenantBookableOffering[]>('/api/v1/web/schedule/bookable-offerings');
  return response.data;
}

export async function createScheduleBookableOffering(
  payload: TenantBookableOfferingPayload
): Promise<TenantBookableOffering> {
  const response = await apiClient.post<TenantBookableOffering>('/api/v1/web/schedule/bookable-offerings', payload);
  return response.data;
}

export async function updateScheduleBookableOffering(
  offeringId: number,
  payload: TenantBookableOfferingPayload
): Promise<TenantBookableOffering> {
  const response = await apiClient.patch<TenantBookableOffering>(
    `/api/v1/web/schedule/bookable-offerings/${offeringId}`,
    payload
  );
  return response.data;
}

export async function getScheduleDashboard(): Promise<ScheduleDashboardResponse> {
  const response = await apiClient.get<ScheduleDashboardResponse>('/api/v1/web/schedule/dashboard');
  return response.data;
}

export async function getSchedulePrograms(): Promise<TenantProgram[]> {
  const response = await apiClient.get<TenantProgram[]>('/api/v1/web/schedule/programs');
  return response.data;
}

export async function getScheduleCoachAvailability(): Promise<TenantCoachAvailability[]> {
  const response = await apiClient.get<TenantCoachAvailability[]>('/api/v1/web/schedule/availability');
  return response.data;
}

export async function createScheduleCoachAvailability(
  payload: TenantCoachAvailabilityPayload
): Promise<TenantCoachAvailability> {
  const response = await apiClient.post<TenantCoachAvailability>('/api/v1/web/schedule/availability', payload);
  return response.data;
}

export async function updateScheduleCoachAvailability(
  availabilityId: number,
  payload: TenantCoachAvailabilityPayload
): Promise<TenantCoachAvailability> {
  const response = await apiClient.patch<TenantCoachAvailability>(
    `/api/v1/web/schedule/availability/${availabilityId}`,
    payload
  );
  return response.data;
}

export async function deleteScheduleCoachAvailability(availabilityId: number): Promise<void> {
  await apiClient.delete(`/api/v1/web/schedule/availability/${availabilityId}`);
}

export async function getScheduleAthleteAvailability(): Promise<TenantAthleteAvailabilityListResponse> {
  const response = await apiClient.get<TenantAthleteAvailabilityListResponse>('/api/v1/web/schedule/student-availability');
  return response.data;
}

export async function createScheduleAthleteAvailability(
  payload: TenantAthleteAvailabilityPayload
): Promise<TenantAthleteAvailability> {
  const response = await apiClient.post<TenantAthleteAvailability>('/api/v1/web/schedule/student-availability', payload);
  return response.data;
}

export async function updateScheduleAthleteAvailability(
  availabilityId: number,
  payload: TenantAthleteAvailabilityPayload
): Promise<TenantAthleteAvailability> {
  const response = await apiClient.patch<TenantAthleteAvailability>(
    `/api/v1/web/schedule/student-availability/${availabilityId}`,
    payload
  );
  return response.data;
}

export async function deleteScheduleAthleteAvailability(availabilityId: number): Promise<void> {
  await apiClient.delete(`/api/v1/web/schedule/student-availability/${availabilityId}`);
}

export async function generateSmartScheduleSuggestions(
  payload: TenantSmartSchedulePayload
): Promise<TenantSmartScheduleSuggestionResponse> {
  const response = await apiClient.post<TenantSmartScheduleSuggestionResponse>(
    '/api/v1/web/schedule/smart-schedule/suggestions',
    payload
  );
  return response.data;
}

export async function createScheduleProgram(payload: TenantProgramUpsertPayload, config?: ApiRequestConfig): Promise<TenantProgram> {
  const response = await apiClient.post<TenantProgram>('/api/v1/web/schedule/programs', payload, config);
  return response.data;
}

export async function updateScheduleProgram(
  programId: number,
  payload: TenantProgramUpsertPayload, config?: ApiRequestConfig
): Promise<TenantProgram> {
  const response = await apiClient.patch<TenantProgram>(`/api/v1/web/schedule/programs/${programId}`, payload, config);
  return response.data;
}

export async function archiveScheduleProgram(programId: number, config?: ApiRequestConfig): Promise<TenantProgram> {
  const response = await apiClient.post<TenantProgram>(`/api/v1/web/schedule/programs/${programId}/archive`, null, config);
  return response.data;
}

export async function restoreScheduleProgram(programId: number, config?: ApiRequestConfig): Promise<TenantProgram> {
  const response = await apiClient.post<TenantProgram>(`/api/v1/web/schedule/programs/${programId}/restore`, null, config);
  return response.data;
}

export async function getScheduleProgramRegistrations(): Promise<TenantProgramRegistration[]> {
  const response = await apiClient.get<TenantProgramRegistration[]>('/api/v1/web/schedule/program-registrations');
  return response.data;
}

export async function updateScheduleProgramRegistration(
  registrationId: number,
  payload: TenantProgramRegistrationUpdatePayload
): Promise<TenantProgramRegistration> {
  const response = await apiClient.patch<TenantProgramRegistration>(
    `/api/v1/web/schedule/program-registrations/${registrationId}`,
    payload
  );
  return response.data;
}

export async function createScheduleOccurrence(payload: TenantScheduleCreatePayload): Promise<ScheduleDashboardOccurrence> {
  const response = await apiClient.post<ScheduleDashboardOccurrence>('/api/v1/web/schedule/occurrences', payload);
  return response.data;
}

export async function updateScheduleOccurrence(
  occurrenceId: number,
  payload: TenantScheduleUpdatePayload
): Promise<ScheduleDashboardOccurrence> {
  const response = await apiClient.patch<ScheduleDashboardOccurrence>(`/api/v1/web/schedule/occurrences/${occurrenceId}`, payload);
  return response.data;
}

export async function cancelScheduleOccurrence(
  occurrenceId: number,
  payload?: TenantScheduleCancelPayload
): Promise<ScheduleDashboardOccurrence> {
  const response = await apiClient.post<ScheduleDashboardOccurrence>(`/api/v1/web/schedule/occurrences/${occurrenceId}/cancel`, payload ?? {});
  return response.data;
}

export async function getScheduleOccurrenceDetail(occurrenceId: number): Promise<TenantSessionDetailResponse> {
  const response = await apiClient.get<TenantSessionDetailResponse>(
    `/api/v1/web/schedule/occurrences/${occurrenceId}/detail`
  );
  return response.data;
}

export async function addScheduleOccurrenceRosterAthlete(
  occurrenceId: number,
  payload: TenantSessionRosterAddPayload
): Promise<TenantSessionDetailResponse> {
  const response = await apiClient.post<TenantSessionDetailResponse>(
    `/api/v1/web/schedule/occurrences/${occurrenceId}/roster`,
    payload
  );
  return response.data;
}

export async function updateScheduleOccurrenceRosterBooking(
  occurrenceId: number,
  bookingId: number,
  payload: TenantSessionRosterUpdatePayload
): Promise<TenantSessionDetailResponse> {
  const response = await apiClient.patch<TenantSessionDetailResponse>(
    `/api/v1/web/schedule/occurrences/${occurrenceId}/roster/${bookingId}`,
    payload
  );
  return response.data;
}

export async function cancelScheduleOccurrenceRosterBookingAndSuggest(
  occurrenceId: number,
  bookingId: number,
  payload?: TenantRosterCancellationPayload
): Promise<TenantSessionFollowUpResponse> {
  const response = await apiClient.post<TenantSessionFollowUpResponse>(
    `/api/v1/web/schedule/occurrences/${occurrenceId}/roster/${bookingId}/cancel-and-suggest`,
    payload ?? {}
  );
  return response.data;
}

export async function getScheduleOccurrenceSlotFillers(
  occurrenceId: number
): Promise<TenantSlotFillerSuggestionResponse> {
  const response = await apiClient.get<TenantSlotFillerSuggestionResponse>(
    `/api/v1/web/schedule/occurrences/${occurrenceId}/slot-fillers`
  );
  return response.data;
}

export async function applyScheduleOccurrenceSlotFiller(
  occurrenceId: number,
  payload: TenantSlotFillerApplyPayload
): Promise<TenantSessionFollowUpResponse> {
  const response = await apiClient.post<TenantSessionFollowUpResponse>(
    `/api/v1/web/schedule/occurrences/${occurrenceId}/slot-fillers/apply`,
    payload
  );
  return response.data;
}

export async function updateScheduleOccurrenceAttendance(
  occurrenceId: number,
  bookingId: number,
  payload: TenantSessionAttendanceUpdatePayload
): Promise<TenantSessionDetailResponse> {
  const response = await apiClient.patch<TenantSessionDetailResponse>(
    `/api/v1/web/schedule/occurrences/${occurrenceId}/attendance/${bookingId}`,
    payload
  );
  return response.data;
}

export async function clearScheduleOccurrenceAttendance(
  occurrenceId: number,
  bookingId: number
): Promise<TenantSessionDetailResponse> {
  const response = await apiClient.delete<TenantSessionDetailResponse>(
    `/api/v1/web/schedule/occurrences/${occurrenceId}/attendance/${bookingId}`
  );
  return response.data;
}

export async function checkoutScheduleOccurrenceAttendance(
  occurrenceId: number,
  bookingId: number
): Promise<TenantSessionDetailResponse> {
  const response = await apiClient.post<TenantSessionDetailResponse>(
    `/api/v1/web/schedule/occurrences/${occurrenceId}/attendance/${bookingId}/checkout`,
    {}
  );
  return response.data;
}

export async function getScheduleOccurrenceAttendanceRecap(
  occurrenceId: number,
  bookingId: number
): Promise<TenantSessionRecapResponse> {
  const response = await apiClient.get<TenantSessionRecapResponse>(
    `/api/v1/web/schedule/occurrences/${occurrenceId}/attendance/${bookingId}/recap`
  );
  return response.data;
}

export async function saveScheduleOccurrenceAttendanceRecap(
  occurrenceId: number,
  bookingId: number,
  payload: TenantSessionRecapPayload
): Promise<TenantSessionRecapMutationResponse> {
  const response = await apiClient.put<TenantSessionRecapMutationResponse>(
    `/api/v1/web/schedule/occurrences/${occurrenceId}/attendance/${bookingId}/recap`,
    payload
  );
  return response.data;
}

export async function finishScheduleOccurrenceAttendanceRecap(
  occurrenceId: number,
  bookingId: number,
  payload: TenantSessionRecapPayload
): Promise<TenantSessionRecapMutationResponse> {
  const response = await apiClient.post<TenantSessionRecapMutationResponse>(
    `/api/v1/web/schedule/occurrences/${occurrenceId}/attendance/${bookingId}/finish`,
    payload
  );
  return response.data;
}

export async function updateScheduleOccurrenceProgressNote(
  occurrenceId: number,
  bookingId: number,
  payload: TenantSessionProgressNoteUpdatePayload
): Promise<TenantSessionDetailResponse> {
  const response = await apiClient.patch<TenantSessionDetailResponse>(
    `/api/v1/web/schedule/occurrences/${occurrenceId}/attendance/${bookingId}/note`,
    payload
  );
  return response.data;
}

export async function markScheduleOccurrenceBookedRosterPresent(
  occurrenceId: number
): Promise<TenantSessionDetailResponse> {
  const response = await apiClient.post<TenantSessionDetailResponse>(
    `/api/v1/web/schedule/occurrences/${occurrenceId}/attendance/mark-booked-present`
  );
  return response.data;
}

export async function updateScheduleOccurrenceMakeupCredit(
  occurrenceId: number,
  bookingId: number,
  payload: TenantMakeupCreditUpdatePayload
): Promise<TenantSessionDetailResponse> {
  const response = await apiClient.patch<TenantSessionDetailResponse>(
    `/api/v1/web/schedule/occurrences/${occurrenceId}/makeups/${bookingId}`,
    payload
  );
  return response.data;
}

export async function getScheduleTimePollOptions(): Promise<TenantTimePollBuilderOptions> {
  const response = await apiClient.get<TenantTimePollBuilderOptions>('/api/v1/web/schedule/time-polls/options');
  return response.data;
}

export async function getScheduleTimePolls(): Promise<TenantTimePoll[]> {
  const response = await apiClient.get<TenantTimePoll[]>('/api/v1/web/schedule/time-polls');
  return response.data;
}

export async function getScheduleTimePoll(pollId: number): Promise<TenantTimePoll> {
  const response = await apiClient.get<TenantTimePoll>(`/api/v1/web/schedule/time-polls/${pollId}`);
  return response.data;
}

export async function createScheduleTimePoll(payload: TenantTimePollSavePayload): Promise<TenantTimePoll> {
  const response = await apiClient.post<TenantTimePoll>('/api/v1/web/schedule/time-polls', payload);
  return response.data;
}

export async function updateScheduleTimePoll(
  pollId: number,
  payload: TenantTimePollSavePayload
): Promise<TenantTimePoll> {
  const response = await apiClient.patch<TenantTimePoll>(`/api/v1/web/schedule/time-polls/${pollId}`, payload);
  return response.data;
}

export async function openScheduleTimePoll(pollId: number): Promise<TenantTimePoll> {
  const response = await apiClient.post<TenantTimePoll>(`/api/v1/web/schedule/time-polls/${pollId}/open`);
  return response.data;
}

export async function closeScheduleTimePoll(pollId: number): Promise<TenantTimePoll> {
  const response = await apiClient.post<TenantTimePoll>(`/api/v1/web/schedule/time-polls/${pollId}/close`);
  return response.data;
}

export async function generateScheduleDraft(pollId: number): Promise<TenantScheduleDraft> {
  const response = await apiClient.post<TenantScheduleDraft>(`/api/v1/web/schedule/time-polls/${pollId}/draft`, {});
  return response.data;
}

export async function getScheduleDraft(draftId: number): Promise<TenantScheduleDraft> {
  const response = await apiClient.get<TenantScheduleDraft>(`/api/v1/web/schedule/drafts/${draftId}`);
  return response.data;
}

export async function updateScheduleDraft(draftId: number, payload: TenantScheduleDraftUpdatePayload): Promise<TenantScheduleDraft> {
  const response = await apiClient.patch<TenantScheduleDraft>(`/api/v1/web/schedule/drafts/${draftId}`, payload);
  return response.data;
}

export async function approveScheduleDraft(draftId: number, version: number, acknowledgeWarnings: boolean): Promise<TenantScheduleDraft> {
  const response = await apiClient.post<TenantScheduleDraft>(`/api/v1/web/schedule/drafts/${draftId}/approve`, { version, acknowledgeWarnings });
  return response.data;
}

export async function discardScheduleDraft(draftId: number, version: number): Promise<TenantScheduleDraft> {
  const response = await apiClient.post<TenantScheduleDraft>(`/api/v1/web/schedule/drafts/${draftId}/discard`, { version });
  return response.data;
}
